import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  ContainerFilled,
  UserOutlined,
  TeamOutlined,
  QuestionCircleOutlined,
  ShoppingFilled,
  CarOutlined,
  FileSearchOutlined,
  LogoutOutlined
} from '@ant-design/icons';
import { Layout, Menu, Avatar, Space, Button } from 'antd';
import decodeToken from '../../Utils/decodeToken';
import SelectorEmpresa from './SelectorEmpresa';
import Notificaciones from './Notificaciones';
import LogoBPMS from '../../Assets/Images/Logo-BPMS-1x.png';
import { useEffect } from 'react';

const { Header, Sider, Content, Footer } = Layout;

const AppLayout = () => {
  let location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(true);
  const [decodedToken, setDecodedToken] = useState(null);
  const [screenSizePage, setScreenSizePage] = useState('');

  const gestionPedidosArea = [
    'ventas',
    'equipo_direccion',
    'ventas_bh',
    'ventas_pl',
    'compras',
    'sgcmc',
    // 'muestras',
    'mesa_de_control',
    'almacen'
  ];

  const userRole = decodedToken?.role?.name;
  const userArea = decodedToken?.user?.display_name_area;

  const isAdmin = userRole === 'administrator';
  const isSuperAdmin = userRole === 'Super_Admin';

  // El backend resuelve quién es el usuario maestro y lo manda en el token, de
  // forma que aquí no haya que comparar contra el nombre de un rol concreto.
  // Se conserva la comparación con 'Super_Admin' como respaldo para los tokens
  // emitidos antes de que existiera el claim, que aún pueden estar vigentes.
  const isMaster = decodedToken?.is_master ?? isSuperAdmin;

  useEffect(() => {
    function handleResize() {
      const xs = window.innerWidth < 576;
      const sm = window.innerWidth < 768;
      const md = window.innerWidth < 992;
      const lg = window.innerWidth < 1200;
      const xl = window.innerWidth < 1500;
      const xxl = window.innerWidth < 2000;

      if (xs) {
        setScreenSizePage('xs');
      } else if (sm) {
        setScreenSizePage('sm');
      } else if (md) {
        setScreenSizePage('md');
      } else if (lg) {
        setScreenSizePage('lg');
      } else if (xl) {
        setScreenSizePage('xl');
      } else if (xxl) {
        setScreenSizePage('xxl');
      } else {
        setScreenSizePage('xxxl');
      }
    }

    handleResize();

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    if (localStorage.getItem('token')) setDecodedToken(decodeToken(localStorage.getItem('token')));
    setCollapsed(true);
  }, [localStorage.getItem('token')]);

  const cerrarSesion = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  // Nombre corto para el sidebar: un nombre y un apellido. El token guarda el nombre
  // completo sin distinguir nombres de apellidos, así que se adivina por posición:
  // con 3 palabras (nombre + dos apellidos) se toma la segunda; con 4 o más se asume
  // que hay dos nombres y se toma la primera palabra del apellido, saltando partículas
  // ("de", "la", "del"...). No es infalible; el nombre completo queda en el tooltip.
  const PARTICULAS = ['de', 'del', 'la', 'las', 'los', 'y', 'san'];
  const palabrasNombre = (decodedToken?.user?.name || '').trim().split(/\s+/).filter(Boolean);
  const indiceApellido =
    palabrasNombre.length <= 3
      ? 1
      : palabrasNombre.findIndex(
          (palabra, i) => i >= 2 && !PARTICULAS.includes(palabra.toLowerCase())
        );
  const nombreCorto =
    palabrasNombre.length > 1 && indiceApellido > 0
      ? `${palabrasNombre[0]} ${palabrasNombre[indiceApellido]}`
      : palabrasNombre[0] || '';

  // Placeholder del avatar cuando no hay foto: iniciales del nombre corto.
  const iniciales = nombreCorto
    .split(' ')
    .map((palabra) => palabra[0])
    .join('')
    .toUpperCase();

  // Opción resaltada del menú según la ruta actual. Las pantallas de detalle no tienen
  // opción propia: el detalle de un gasto pertenece a "Solicitudes de gasto".
  const RUTAS_DEL_MENU = [
    '/pedidos',
    '/rutas-de-entrega',
    '/solicitudes-gasto',
    '/capacitacion',
    '/users',
    '/logs'
  ];
  const claveActiva =
    location.pathname.startsWith('/detalle-gasto') ||
    location.pathname.startsWith('/crear-solicitud-gasto')
      ? '/solicitudes-gasto'
      : RUTAS_DEL_MENU.find(
          (ruta) => location.pathname === ruta || location.pathname.startsWith(`${ruta}/`)
        ) || '';

  // Menú en tres grupos. Los permisos son los de siempre: Operación y Administración solo
  // existen para quien ve el menú completo; el resto solo ve el grupo de gastos.
  const veMenuCompleto =
    userRole === 'Super_Admin' || userArea === 'sgcmc' || decodedToken?.sub === 280;

  const grupoOperacion = [
    isAdmin || isSuperAdmin || gestionPedidosArea.includes(userArea)
      ? { key: '/pedidos', icon: <ContainerFilled />, label: 'Pedidos' }
      : null,
    (isAdmin && userArea !== 'direccion') ||
    userArea === 'compras' ||
    userArea === 'sgcmc' ||
    userArea === 'logistica' ||
    userRole === 'equipo_mesa_control' ||
    isSuperAdmin
      ? { key: '/rutas-de-entrega', icon: <CarOutlined />, label: 'Rutas de entrega' }
      : null
  ].filter(Boolean);

  const grupoGastos = [
    { key: '/solicitudes-gasto', icon: <ShoppingFilled />, label: 'Solicitudes de gasto' },
    { key: '/capacitacion', icon: <QuestionCircleOutlined />, label: 'Capacitación' }
  ];

  const grupoAdministracion = [
    isSuperAdmin ? { key: '/users', icon: <TeamOutlined />, label: 'Usuarios' } : null,
    isMaster ? { key: '/logs', icon: <FileSearchOutlined />, label: 'Logs' } : null
  ].filter(Boolean);

  const gruposMenu = [
    veMenuCompleto && { label: 'Operación', children: grupoOperacion },
    { label: 'Gastos', children: grupoGastos },
    veMenuCompleto && { label: 'Administración', children: grupoAdministracion }
  ].filter((grupo) => grupo && grupo.children.length > 0);

  // Con un solo grupo el rótulo no aporta nada: la lista va sin él.
  const itemsMenu =
    gruposMenu.length === 1
      ? gruposMenu[0].children
      : gruposMenu.map((grupo) => ({ type: 'group', key: grupo.label, ...grupo }));

  //returns an object with keys xs, sm, md, lg, xl, xxl to check for screen size
  const screenSizes = screenSizePage;

  return (
    <Layout>
      <Sider
        width={
          screenSizes === 'xs' || screenSizes === 'sm' || screenSizes === 'md' ? '250px' : '232px'
        }
        trigger={null}
        collapsible
        collapsed={collapsed}
        breakpoint="lg"
        onBreakpoint={(broken) => {
          setCollapsed(broken);
        }}
        collapsedWidth={
          screenSizes === 'xs'
            ? '0'
            : '80' && screenSizes === 'sm'
            ? '0'
            : '80' && screenSizes === 'md'
            ? '0'
            : '80'
        }
        className="app-sider"
        style={{
          overflow: 'auto',
          height: '100vh',
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 100
        }}>
        <div
          role="group"
          aria-label={`${decodedToken?.user?.name || ''}, ${
            decodedToken?.role?.display_name || ''
          }`}
          className={`section-user-info${collapsed ? ' section-user-info--collapsed' : ''}`}
          title={`${decodedToken?.user?.name || ''} · ${decodedToken?.role?.display_name || ''}`}>
          <Avatar
            className="section-user-info-avatar"
            size={40}
            src={decodedToken?.user?.photo || undefined}
            alt=""
            icon={iniciales ? undefined : <UserOutlined />}>
            {iniciales}
          </Avatar>
          {!collapsed && (
            <div className="section-user-info-text">
              <span className="section-user-info-name">{nombreCorto}</span>
              <span className="section-user-info-role">
                {(decodedToken?.role?.display_name || '').replace(/_/g, ' ')}
              </span>
            </div>
          )}
        </div>

        {/* Solo aparece para quien tiene cuenta en dos empresas; el resto no ve nada. */}
        <SelectorEmpresa accounts={decodedToken?.accounts} collapsed={collapsed} />

        {decodedToken && <Notificaciones collapsed={collapsed} />}

        <Menu
          theme="dark"
          className="sidebar-menu"
          mode="inline"
          selectedKeys={[claveActiva]}
          onClick={({ key }) => {
            navigate(key);
            if (screenSizes === 'xs' || screenSizes === 'sm' || screenSizes === 'md')
              setCollapsed(true);
          }}
          items={itemsMenu}
        />

        <div className="sidebar-salir-zona">
          <button
            type="button"
            className="sidebar-salir"
            onClick={cerrarSesion}
            aria-label="Cerrar sesión"
            title={collapsed ? 'Cerrar sesión' : undefined}>
            <LogoutOutlined aria-hidden="true" />
            {!collapsed && <span>Cerrar sesión</span>}
          </button>
        </div>
      </Sider>
      {/* Tamaño de gris en pantalla */}
      <Layout
        className="site-layout flex"
        style={{
          marginLeft:
            (screenSizes === 'xs' || screenSizes === 'sm' || screenSizes === 'md') && collapsed
              ? '0px'
              : (screenSizes === 'xs' || screenSizes === 'sm' || screenSizes === 'md') && !collapsed
              ? '0px'
              : (screenSizes === 'lg' || screenSizes === 'xl' || screenSizes === 'xxl') &&
                !collapsed
              ? '232px'
              : '80px',

          opacity:
            (screenSizes === 'xs' && !collapsed) ||
            (screenSizes === 'sm' && !collapsed) ||
            (screenSizes === 'md' && !collapsed)
              ? '0.6'
              : ' '
        }}>
        <Header
          className="site-layout-background"
          style={{
            position: 'fixed',
            top: 0,
            zIndex: 99,
            width: '100%',
            padding: 0
          }}>
          {(screenSizes === 'xs' && !collapsed) ||
          (screenSizes === 'sm' && !collapsed) ||
          (screenSizes === 'md' && !collapsed) ? (
            <Button
              style={{ width: '100%', height: '100vh', opacity: '0.1' }}
              onClick={() => setCollapsed(!collapsed)}>
              cdcd
            </Button>
          ) : null}

          <Space
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              paddingRight:
                screenSizes === 'xs' || screenSizes === 'sm' || screenSizePage === 'md'
                  ? '0px'
                  : collapsed
                  ? '80px'
                  : '232px'
            }}>
            <div className="flex">
              {React.createElement(collapsed ? MenuUnfoldOutlined : MenuFoldOutlined, {
                className: 'trigger',
                onClick: () => setCollapsed(!collapsed)
              })}
              <img src={LogoBPMS} width="50px" className="align-self-center mb-1" />
            </div>
          </Space>
        </Header>
        <Content
          className={`site-layout-background container align-self-center${
            location.pathname === '/solicitudes-gasto' ||
            location.pathname === '/users' ||
            location.pathname === '/logs' ||
            location.pathname === '/reporte-general' ||
            location.pathname === '/nueva-solicitud' ||
            location.pathname.startsWith('/detalle-gasto')
              ? ' container--ancho'
              : ''
          }`}>
          <Outlet />
        </Content>
        <Footer
          style={{
            textAlign: 'center',
            opacity:
              (screenSizes === 'xs' && !collapsed) ||
              (screenSizes === 'sm' && !collapsed) ||
              (screenSizes === 'md' && !collapsed)
                ? '0.6'
                : ''
          }}>
          © 2024 BPMS PL - BH. Todos los derechos reservados.
        </Footer>
      </Layout>
    </Layout>
  );
};
export default AppLayout;
